import React, { useState } from 'react'

import { Box, Flex } from '@chakra-ui/react'

type ScoreIndicatorProps = {
  score: number
  maxScore?: number
  partId?: string
  isEditable?: boolean
  onScoreChange?: (score: number) => void
}

const getScoreColor = (score: number): string => {
  const colorMap: Record<number, string> = {
    1: '#C00',
    2: '#F60',
    3: '#FC0',
    4: '#9C0',
    5: '#090',
  }
  return colorMap[score] || '#E0E0E0'
}

export const ScoreIndicator: React.FC<ScoreIndicatorProps> = ({
  score,
  maxScore = 5,
  isEditable = true,
  onScoreChange,
}) => {
  const [hoverScore, setHoverScore] = useState<number | null>(null)

  const displayScore = hoverScore !== null ? hoverScore : score
  const fillPercentage = (displayScore / maxScore) * 100
  const scoreColor = getScoreColor(displayScore)

  const handleSegmentClick = (segmentNumber: number) => {
    if (isEditable && onScoreChange) {
      onScoreChange(segmentNumber)
    }
  }

  return (
    <Flex alignItems={'center'} gap={3} flexShrink={0}>
      <Box
        position={'relative'}
        width={{
          base: '88px',
          sm: '100px',
        }}
        height={'30px'}
        paddingY={'10px'}
        onMouseLeave={() => isEditable && setHoverScore(null)}
        flexShrink={0}
      >
        <Box
          position={'absolute'}
          width="100%"
          height={'10px'}
          top={'10px'}
          borderRadius={'40px'}
          backgroundColor={'#E0E0E0'}
          overflow={'hidden'}
        >
          <Box
            width={`${fillPercentage}%`}
            height={'100%'}
            backgroundColor={scoreColor}
          />
        </Box>
        {[20, 40, 60, 80].map((position) => (
          <Box
            key={position}
            position={'absolute'}
            left={`calc(${position}% - 1px)`}
            top={'11px'}
            width={'2px'}
            height={'8px'}
            rounded={'full'}
            backgroundColor={'rgba(255, 255, 255, 0.5)'}
            zIndex={1}
          />
        ))}
        {isEditable &&
          [1, 2, 3, 4, 5].map((segmentNumber) => (
            <Box
              key={segmentNumber}
              position={'absolute'}
              left={`${(segmentNumber - 1) * 20}%`}
              top={0}
              width="20%"
              height={'30px'}
              cursor={'pointer'}
              onMouseEnter={() => setHoverScore(segmentNumber)}
              onClick={() => handleSegmentClick(segmentNumber)}
              zIndex={2}
            />
          ))}
      </Box>
      <Flex alignItems={'center'} gap={0} flexShrink={0}>
        <Box
          width={'15px'}
          textAlign={'right'}
          fontWeight={'bold'}
          fontSize={'20px'}
          lineHeight={1}
        >
          {displayScore}
        </Box>
        <Box
          width={'10px'}
          textAlign={'center'}
          fontWeight={'bold'}
          fontSize={'20px'}
          lineHeight={1}
        >
          /
        </Box>
        <Box
          width={'15px'}
          textAlign={'left'}
          fontWeight={'bold'}
          fontSize={'20px'}
          lineHeight={1}
        >
          {maxScore}
        </Box>
      </Flex>
    </Flex>
  )
}
